import { Button, Flex, Modal, ScrollArea, SimpleGrid } from "@mantine/core";
import { useCallback, useState } from "react";

import { useCreateLimitRequestException } from "../../../../api/hooks/limitRequestException/useCreateLimitRequestException.ts";
import { useUpdateLimitRequestException } from "../../../../api/hooks/limitRequestException/useUpdateLimitRequestException.ts";
import MultiFieldCard from "../../../../components/Cards/MultiFieldCard/MultiFieldCard.tsx";
import SvgPlus from "../../../../components/Icons/Plus.tsx";
import { UpdateLimitRequestExceptionInput } from "../../../../graphql/generated.ts";
import { useMutationNotificationWrapper } from "../../../../hooks/useMutationNotificationWrapper.tsx";
import { LimitRequestException } from "../../../../types/limitRequestException.ts";
import ExceptionForm from "./ExceptionForm.tsx";
import { useExceptionsList } from "./hooks/useExceptionsList.tsx";

interface Props {
  limitRequestId: number;
  data: LimitRequestException[];
  opened: boolean;
  onClose: () => void;
}

const ExceptionModal = ({ limitRequestId, data, opened, onClose }: Props) => {
  const [items, setItems] = useState(data);
  const [selectedItem, setSelectedItem] =
    useState<LimitRequestException | null>(null);
  const [openForm, setOpen] = useState<boolean>(false);

  const [, updateException] = useMutationNotificationWrapper(
    useUpdateLimitRequestException(),
    {
      success: { message: "Exception was successfully updated." },
    }
  );

  const [, createException] = useMutationNotificationWrapper(
    useCreateLimitRequestException(),
    {
      success: { message: "Exception was successfully created." },
    }
  );

  const handleSave = useCallback(
    (input: UpdateLimitRequestExceptionInput, id?: number) => {
      if (id) {
        updateException({ id, input }).then((res) => {
          const updatedException = res.data?.updateLimitRequestException;
          setItems((prevItems) =>
            prevItems.map((item) =>
              item.id === id ? updatedException ?? item : item
            )
          );
        });
        return;
      }

      createException({
        input: { ...input, limitRequestId },
      }).then((res) => {
        const createdException = res?.data?.createLimitRequestException;
        if (!createdException) {
          return;
        }
        setItems((prevItems) => [...prevItems, createdException]);
      });
    },
    [createException, limitRequestId, updateException]
  );

  const handleCloseForm = useCallback(() => {
    setOpen(false);
    setSelectedItem(null);
  }, [setOpen, setSelectedItem]);

  const handleSubmitForm = useCallback(
    (value: UpdateLimitRequestExceptionInput, id?: number) => {
      handleSave(value, id);
      handleCloseForm();
    },
    [handleSave, handleCloseForm]
  );

  const handleOpen = () => setOpen(true);

  const handleEditForm = (value: LimitRequestException) => {
    setSelectedItem(value);
    handleOpen();
  };

  const exceptionItems = useExceptionsList({
    data: items,
    onEdit: handleEditForm,
  });

  return (
    <Modal
      opened={opened}
      title={!openForm || selectedItem ? "Edit Exceptions" : "Add Exceptions"}
      closeButtonProps={{
        size: 24,
        iconSize: 24,
      }}
      scrollAreaComponent={ScrollArea.Autosize}
      size="lg"
      onClose={onClose}
    >
      {openForm ? (
        <ExceptionForm
          value={selectedItem}
          onClose={handleCloseForm}
          onSubmit={handleSubmitForm}
        />
      ) : (
        <Flex direction="column" gap={20}>
          <Flex direction="column" gap={8}>
            {exceptionItems && (
              <SimpleGrid w="100%" cols={2} spacing={8} verticalSpacing={8}>
                {exceptionItems.map((item, index) => (
                  <MultiFieldCard key={index} {...item} />
                ))}
              </SimpleGrid>
            )}
            <Button
              variant="outlineBlue"
              leftIcon={<SvgPlus />}
              uppercase
              onClick={handleOpen}
            >
              Add
            </Button>
          </Flex>
          <Flex gap={20} justify="end">
            <Button variant="outlineBlue" uppercase onClick={onClose}>
              cancel
            </Button>
            <Button variant="filledBlue" uppercase onClick={onClose}>
              save
            </Button>
          </Flex>
        </Flex>
      )}
    </Modal>
  );
};

export default ExceptionModal;
